import * as React from 'react'
import { styled, css } from 'next-yak'

import * as plume from '@ulule/owl-kit-components/next'

import { t } from '@owl-nest/localize'
import { Lang } from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'
import { MetaSettingDropdown } from '@owl-nest/metasettings'
import * as env from '@owl-nest/config'
import { PolymorphicComponent, YakComponentOf } from '@owl-nest/yak/extra-runtime'

import { AccordionSection } from './AccordionSection'
import { NavSection } from '../baseConfig'
import { footerBreakpoint } from './constants'

type BodyProps = {
  className?: string
  style?: React.CSSProperties
  controlledCurrency?: string
  hasCurrencyAndLang: boolean
  lang: Lang
  $lang: Lang
  navSections: NavSection[]
  withRedirectParam?: boolean
}

const PROMISE = Promise.all([env.ALLOWED_LANGUAGES, env.ALLOWED_CURRENCIES])

function BodyComponent({
  className,
  style,
  controlledCurrency,
  lang,
  navSections,
  hasCurrencyAndLang,
  withRedirectParam,
}: BodyProps): React.ReactElement<BodyProps> {
  const settings = hooks.useUserConfig(env.ULULE_ORIGINS[lang])

  const promiseStatus = hooks.useAwait(PROMISE)

  const currentLanguage = settings.data?.lang
  const currentCurrency = controlledCurrency ?? settings.data?.currency

  if (promiseStatus.status !== hooks.AwaitStatus.SUCCESS) {
    return <></>
  }

  const languages = promiseStatus.success[0]
  const currencies = promiseStatus.success[1]

  return (
    <div className={className} style={style}>
      {hasCurrencyAndLang && (
        <LangAndCurrencyWrapper $lang={lang}>
          <plume.styles.copy.S.semiBold>{t('Language and site currency')}</plume.styles.copy.S.semiBold>
          {currentCurrency && currentLanguage && (
            <MetaSettingDropdown
              languages={languages}
              currentLanguage={currentLanguage}
              currencies={currencies}
              currentCurrency={currentCurrency}
              withRedirectParam={withRedirectParam}
            />
          )}
        </LangAndCurrencyWrapper>
      )}
      <Sections $lang={lang}>
        {navSections.map((navConfig, i) => {
          return <Section key={i} navConfig={navConfig} lang={lang} />
        })}
      </Sections>
    </div>
  )
}

type SectionProps = {
  lang: Lang
  navConfig: NavSection
}

function Section({ navConfig, lang }: SectionProps) {
  return (
    <LinkColumn $lang={lang} title={navConfig.href ? <a href={navConfig.href}>{navConfig.title}</a> : navConfig.title}>
      <LinkList>
        {navConfig.items
          .filter((navConfig) => {
            if (navConfig.language) {
              return lang === navConfig.language
            }
            return true
          })
          .map((item, i) => {
            return (
              <LinkItem $lang={lang} key={i} as="li">
                <plume.Link href={item.href} target={`${item.openInNewTab ? '_blank' : '_self'}`}>
                  {item.label}
                </plume.Link>
              </LinkItem>
            )
          })}
      </LinkList>
    </LinkColumn>
  )
}

const langAndCurrencyWrapperBreakpointStyle = css`
  display: flex;
  flex-wrap: nowrap;

  ${plume.styles.copy.S.semiBold} {
    margin-bottom: 0;
    min-width: auto;
  }
`

const LangAndCurrencyWrapper = styled.div<{ $lang: string }>`
  align-items: end;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 29px;

  ${plume.styles.copy.S.semiBold} {
    color: ${plume.COLORS.TEXT_TERTIARY};
    margin-bottom: 16px;
    margin-right: 18px;
    min-width: 100%;
  }

  ${plume.Dropdown} {
    margin-right: 16px;
  }

  ${({ $lang }) => {
    if (footerBreakpoint($lang) === 'laptop') {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          ${langAndCurrencyWrapperBreakpointStyle};
        }
      `
    }
    if (footerBreakpoint($lang) === 'tablet') {
      return css`
        @media screen and ${plume.BREAKPOINTS.TABLET} {
          ${langAndCurrencyWrapperBreakpointStyle};
        }
      `
    }
  }}
`

const LinkItem = styled(plume.styles.copy.S.regular)<{ $lang: string }>`
  margin-bottom: 16px;

  ${plume.Link} {
    font-weight: 400; // HACK: irregular font manipulation
  }

  &:first-child {
    margin-top: 16px;
  }

  ${({ $lang }) => {
    if (footerBreakpoint($lang) === 'laptop') {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          margin-left: 0px;
          margin-bottom: 8px;
        }
      `
    }
    if (footerBreakpoint($lang) === 'tablet') {
      return css`
        @media screen and ${plume.BREAKPOINTS.TABLET} {
          margin-left: 0px;
          margin-bottom: 8px;
        }
      `
    }
  }}
` as YakComponentOf<PolymorphicComponent<'p', plume.styles.copy.CopyProps & { $lang: string }>>

const LinkList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`

const LinkColumn = styled(AccordionSection)<{ $lang: string }>`
  margin-bottom: 15px;

  ${({ $lang }) => {
    if (footerBreakpoint($lang) === 'laptop') {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          margin-bottom: 0px;
        }
      `
    }
    if (footerBreakpoint($lang) === 'tablet') {
      return css`
        @media screen and ${plume.BREAKPOINTS.TABLET} {
          margin-bottom: 0px;
        }
      `
    }
  }}
`

const Sections = styled.div<{ $lang: string }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${({ $lang }) => {
    if (footerBreakpoint($lang) === 'laptop') {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          flex-direction: row;
        }
      `
    }
    if (footerBreakpoint($lang) === 'tablet') {
      return css`
        @media screen and ${plume.BREAKPOINTS.TABLET} {
          flex-direction: row;
        }
      `
    }
  }}
`

export const Body = styled(BodyComponent)<{ $lang: Lang }>`
  margin: 0 auto;
  max-width: 450px;
  padding: 33px 20px 0;

  ${({ $lang }) => {
    if (footerBreakpoint($lang) === 'laptop') {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          max-width: ${({ $lang }) => ($lang === 'fr' ? '900px' : '700px')};
          padding: 30px 0 20px 0;
        }
      `
    }
    if (footerBreakpoint($lang) === 'tablet') {
      return css`
        @media screen and ${plume.BREAKPOINTS.TABLET} {
          max-width: ${({ $lang }) => ($lang === 'fr' ? '900px' : '700px')};
          padding: 30px 0 20px 0;
        }
      `
    }
  }}
`
